
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUserStore } from "@/store/userStore";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import DebugModal from "../debug/DebugModal";
import { getPrimaryRole } from "@/hooks/useUserRoles";

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole: 'admin' | 'coach' | 'athlete';
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children, requiredRole }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { role, setRole, gymId } = useUserStore();
  const [isCheckingRole, setIsCheckingRole] = useState(true);
  const [debugError, setDebugError] = useState<any>(null);
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  // Dernier couple (user, salle) dont le rôle a été vérifié : tant qu'il est
  // identique, inutile de repasser par l'edge function — et surtout par
  // l'écran « Loading… » qui démonte toute la route (perte des formulaires
  // ouverts à chaque retour de background, où Supabase rafraîchit le token).
  const lastCheckedRef = useRef<{ userId: string; gymId: string } | null>(null);

  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) {
        setIsCheckingRole(false);
        return;
      }
      if (!gymId) {
        console.log("AuthGuard: Waiting for gym_id to load...");
        setIsCheckingRole(true);
        return;
      }

      if (
        role &&
        lastCheckedRef.current &&
        lastCheckedRef.current.userId === user.id &&
        lastCheckedRef.current.gymId === gymId
      ) {
        setIsCheckingRole(false);
        return;
      }

      setIsCheckingRole(true);
      console.log("AuthGuard: Checking roles with gym_id:", gymId);
      
      
      try {
        // Get user roles via edge function
        const { data, error } = await supabase.functions.invoke('user-management', {
          body: {
            action: 'get_user_roles',
            gym_id: gymId
          }
        });
        
        if (error) {
          console.error("AuthGuard: Error fetching user roles:", error);
          setDebugError({
            type: 'Roles Error',
            error,
            userId: user.id,
            currentRole: role,
            requiredRole,
            location: location.pathname
          });
          setRole('athlete'); // Default fallback
        } else {
          const userRoles = data.roles || [];
          
          if (userRoles.length === 0) {
            console.log("AuthGuard: No roles found, defaulting to athlete");
            setRole('athlete');
          } else {
            const userRole = getPrimaryRole(userRoles as any[]);
            console.log("AuthGuard: User role determined:", userRole, "Roles:", userRoles);
            setRole(userRole);
          }
        }
      } catch (error) {
        console.error("AuthGuard: Unexpected error checking user role:", error);
        setRole('athlete'); // Default fallback
      } finally {
        lastCheckedRef.current = { userId: user.id, gymId };
        setIsCheckingRole(false);
      }
    };

    if (user) {
      checkUserRole();
    } else {
      setIsCheckingRole(false);
    }
    // user?.id (et non user) : l'objet est recréé à chaque TOKEN_REFRESHED
    // alors que l'identité du compte, elle, n'a pas changé.
  }, [user?.id, gymId, setRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show loading while checking auth status or user role
  if (authLoading || isCheckingRole || (user && !gymId)) {
    console.log("AuthGuard: Loading...", { authLoading, isCheckingRole, hasUser: !!user, hasGymId: !!gymId });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600 mb-2">Loading...</p>
        </div>
      </div>
    );
  }

  // If not logged in, redirect to login
  if (!user) {
    console.log("AuthGuard: User not logged in, redirecting to login");
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // If logged in but wrong role, show error and redirect
  if (role !== requiredRole) {
    console.log(`AuthGuard: Wrong role. Required: ${requiredRole}, Current: ${role}`);
    toast.error(t('auth:unauthorizedAccess'));
    
    // Redirect based on user role
    let redirectPath = '/login';
    if (role === 'admin') {
      redirectPath = '/admin/dashboard';
    } else if (role === 'coach') {
      redirectPath = '/coach/dashboard';
    } else if (role === 'athlete') {
      redirectPath = '/athlete/dashboard';
    }
    
    console.log("AuthGuard: Redirecting to:", redirectPath);
    return <Navigate to={redirectPath} replace />;
  }

  // If the right role, show the route
  console.log("AuthGuard: Correct role, rendering children");
  return (
    <>
      {children}
      <DebugModal 
        isOpen={isDebugModalOpen}
        onClose={() => setIsDebugModalOpen(false)}
        error={debugError}
      />
    </>
  );
};
